import type {
  CurrentAuthState,
  OrgInfo,
  Warehouse,
  PolicyConfig,
  UserInfo,
} from "./state";
import {
  AWAITING_ORG_SELECT,
  AWAITING_PASSWORD,
  CREATING_SESSION,
  AUTHENTICATED,
} from "./state";

function getOrgsFromState(state: CurrentAuthState): OrgInfo[] {
  switch (state.status) {
    case AWAITING_ORG_SELECT:
    case AWAITING_PASSWORD:
    case CREATING_SESSION:
    case AUTHENTICATED:
      return state.orgs;
    default:
      return [];
  }
}

export function getOrgs(state: CurrentAuthState): OrgInfo[] {
  return getOrgsFromState(state);
}

export function getOrgById(
  state: CurrentAuthState,
  orgId: string,
): OrgInfo | null {
  return getOrgsFromState(state).find((o) => o.org_id === orgId) ?? null;
}

export function getOrgName(
  state: CurrentAuthState,
  orgId: string,
): string | null {
  return getOrgById(state, orgId)?.org_name ?? null;
}

export function getOrgSlug(state: CurrentAuthState, orgId: string): string {
  const org = getOrgById(state, orgId);
  return org?.org_slug || orgId;
}

export function getOrgIdByStub(
  state: CurrentAuthState,
  orgStub: string,
): string | null {
  return (
    getOrgsFromState(state).find((o) => o.org_slug === orgStub)?.org_id ?? null
  );
}

export function getLocations(
  state: CurrentAuthState,
  orgId: string,
): Warehouse[] {
  return getOrgById(state, orgId)?.warehouses ?? [];
}

export function getLocationName(
  state: CurrentAuthState,
  orgId: string,
  locationId: string,
): string | null {
  return (
    getLocations(state, orgId).find((l) => l.warehouseId === locationId)
      ?.locationName ?? null
  );
}

export function getTenantId(
  state: CurrentAuthState,
  orgId: string,
  locationId: string,
): string | null {
  return (
    getLocations(state, orgId).find((l) => l.warehouseId === locationId)
      ?.tenantId ?? null
  );
}

export function getWarehouseId(
  state: CurrentAuthState,
  orgId: string,
  locationId: string,
): string | null {
  return (
    getLocations(state, orgId).find((l) => l.warehouseId === locationId)
      ?.warehouseId ?? null
  );
}

export function hasPermission(
  policyConfig: PolicyConfig | null,
  userInfo: UserInfo | null,
  resource: string,
  action: string,
): boolean {
  if (!policyConfig || !userInfo) return false;

  const userRoles = userInfo.roles ?? [];

  for (const roleName of userRoles) {
    const role = policyConfig.roles?.find((r) => r.id === roleName);
    if (!role) continue;

    const permission = role.permissions?.find((p) => p.id === resource);
    if (permission && permission.actions?.includes(action)) {
      return true;
    }
  }

  return false;
}

export function getFeatureFlag(
  userInfo: UserInfo | null,
  orgId: string | null,
  featureFlag: string,
): boolean {
  if (!userInfo || !orgId) return false;
  const orgFlags = userInfo.feature_flags?.organizations[orgId];
  if (!orgFlags) return false;
  return orgFlags[featureFlag] ?? false;
}
